import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, FormField, Modal, TextInput } from "@a2z/ui";
import { useI18n, useTranslate } from "@/lib/i18n";
import { ApiError } from "@/lib/api/client";
import { endpoints } from "@/lib/api/endpoints";
import type { DispatchAssignmentCommandResponse } from "@/lib/api/dispatchTypes";
import { useErrorMessage } from "@/shared/errors";
import { useMoney } from "@/shared/ui";
import { roundMoney } from "../lib/pricing";
import { isolate, orderDriversForPicker } from "../lib/dispatchBoard";
import { dispatchQueryKeys, useDispatchAccess, useDispatchBoard } from "../lib/useDispatchBoard";
import { useDispatchCommand } from "../lib/useDispatchCommand";
import type { Invoice } from "@/lib/api/types";

/** The server's bound on the name (`HANDED_TO_MAX_LENGTH`). */
export const HANDED_TO_MAX = 64;

/** The assign refusals the dialog can meet, in the dispatch block's words. */
const ASSIGN_REFUSALS = {
  DISPATCH_DRIVER_NOT_AVAILABLE: "errorNotAvailable",
  DISPATCH_INVOICE_ALREADY_ASSIGNED: "errorAlreadyAssigned",
  DISPATCH_INVOICE_NOT_DELIVERABLE: "errorNotDeliverable",
  DISPATCH_COMMAND_ID_CONFLICT: "errorConflict",
} as const;

/** What the cashier decided: who the bag left with, and whether the platform pays. */
export type HandOverDecision = {
  /** Trimmed; `null` when nothing was typed. */
  handedTo: string | null;
  /**
   * True on the platform bill's primary action: close, then settle AGGREGATOR
   * for what is owed. False is the ordinary close, which leaves the cheque out.
   */
  toPlatform: boolean;
};

/**
 * CLOSING A BILL AT NO TABLE (T16, 2026-10-06) — the close confirmation an
 * off-premise bill gets, where a seated one gets none.
 *
 * Closing a takeaway or a delivery is the moment the bag leaves: with a rider,
 * with the customer at the counter, with a platform's courier. The ops ruling
 * is "OPEN = cooking, CLOSED = bag left, money owed", and the one fact the
 * floor wanted written at that moment is WHO IT LEFT WITH — so the strip's
 * closed tile can read "out with Hassan" when the phone rings. A FIELD, not a
 * lifecycle: a free-text name, optional, no rider entity, no dispatch, no
 * out-for-delivery state (the founder's fence). Recent names come from the
 * strip's own CLOSED entries, as chips — the two riders on tonight — and are
 * stored nowhere else.
 *
 * ON A PLATFORM BILL the primary action is "Close & hand to ⟨platform⟩": the
 * platform took the customer's money, so the bill is closed AND settled as
 * AGGREGATOR for what is owed in one tap, through the same two commands the
 * settle sheet sends in sequence (`closeInvoice`, then `settleInvoice` on the
 * version the close returned). The ordinary close stays beside it as a
 * secondary action, for the platform order whose rider collects cash.
 *
 * THREE CASES, NOT ONE (founder, 2026-10-08). A DIRECT takeaway never reaches
 * this dialog: it always leaves with the customer, so the till settles it in
 * one act, like a table. A takeaway a delivery app brought opens it with
 * `askWho={false}`: no name field, no recent names, and only "Close & hand to
 * ⟨platform⟩" beside Cancel — the platform's courier collects it and the
 * platform pays. A DELIVERY, direct or on a platform, keeps the whole dialog.
 *
 * WITH THE APP DRIVER (driver backend B3 · P14, plan D-12). On the restaurant's
 * own delivery (no platform), for a session that may read the dispatch board:
 *  - the bag already offered to / accepted by a driver: the name is the
 *    DRIVER's, shown as the assigned driver and not typed, and written as
 *    `handedTo` so the receipt, the rider's sitting and the Z's rider block
 *    keep working; an offer not accepted yet says so (the driver picks it up
 *    once they accept — pickup needs the bill closed, which this is);
 *  - no driver yet: today's typed field and recent names, plus — for
 *    `DISPATCH_MANAGE` — the drivers on shift as chips that ASSIGN then
 *    close (the offer first; if it is refused nothing is closed).
 * The cashier still closes the bill: dispatch never closes a bill.
 */
export function HandOverDialog({
  invoice,
  runningTotal,
  platformName,
  recentNames,
  isSubmitting,
  onCancel,
  onConfirm,
  askWho = true,
  dispatchInvoiceId,
}: {
  invoice: Pick<Invoice, "channel" | "aggregatorId" | "total" | "tenderedTotal">;
  /**
   * What the bill will CLOSE at — the page's live running total, fee included.
   * NOT `invoice.total`: that is the STORED total and is 0 on an OPEN bill
   * (reviewer, 2026-10-07), so the "hand to the platform for X" sentence read
   * 0.00 on every bill it was offered on. The settle itself always used the
   * close's own returned figure; only the hint was wrong.
   */
  runningTotal: number;
  /** The platform's NAME, or null on a direct order. */
  platformName: string | null;
  /** Who recent bags left with — the strip's CLOSED entries, newest first. */
  recentNames: readonly string[];
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: (decision: HandOverDecision) => void | Promise<void>;
  /**
   * False on a takeaway a delivery app brought (founder, 2026-10-08): nobody
   * to name, so no name field and no recent-name chips, and the only close
   * offered is the platform hand-over. Defaults to true (a delivery).
   */
  askWho?: boolean;
  /**
   * B3 · P14: the bill's id, to read its driver from the dispatch board and
   * to offer it to one. Absent → today's typed dialog.
   */
  dispatchInvoiceId?: string;
}) {
  const t = useTranslate();
  const { locale } = useI18n();
  const money = useMoney();
  const errorMessage = useErrorMessage();
  const queryClient = useQueryClient();
  const access = useDispatchAccess();
  const { send, busy } = useDispatchCommand();
  const [handedTo, setHandedTo] = useState("");
  const [picked, setPicked] = useState<{ sellerId: string; name: string } | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);
  /* Reviewer L4: an assign that succeeded is remembered, so a retry after a
     failed close goes straight to the close (no second assign → 409). */
  const [assignedTo, setAssignedTo] = useState<string | null>(null);
  const onPlatform = invoice.aggregatorId !== null && platformName !== null;
  const ownDelivery = askWho && invoice.aggregatorId === null && Boolean(dispatchInvoiceId);
  const dispatch = useDispatchBoard({ enabled: ownDelivery });
  const current =
    ownDelivery && dispatchInvoiceId ? (dispatch.lookup(dispatchInvoiceId)?.current ?? null) : null;
  /* The driver who has the bag before it leaves: offered or accepted. */
  const assigned =
    current && (current.status === "OFFERED" || current.status === "ACCEPTED") ? current : null;
  const onShift =
    ownDelivery && !assigned && access.canManage && dispatch.board
      ? orderDriversForPicker(dispatch.board.drivers, locale).filter((driver) => driver.onShift)
      : [];
  const due = Math.max(0, roundMoney(runningTotal - invoice.tenderedTotal));
  const decision = (toPlatform: boolean): HandOverDecision => ({
    handedTo: !askWho
      ? null
      : assigned
        ? assigned.driverName.trim().slice(0, HANDED_TO_MAX) || null
        : handedTo.trim().slice(0, HANDED_TO_MAX) || null,
    toPlatform,
  });

  /* Assign-then-close: the offer first, and only on its success the close. */
  const closePlain = async () => {
    setAssignError(null);
    if (picked && dispatchInvoiceId && !assigned) {
      if (assignedTo !== picked.sellerId) {
      try {
        await send<DispatchAssignmentCommandResponse>(
          `assign:${dispatchInvoiceId}:${picked.sellerId}`,
          endpoints.restaurant.dispatchAssignments,
          { invoiceId: dispatchInvoiceId, driverSellerId: picked.sellerId },
        );
        setAssignedTo(picked.sellerId);
        void queryClient.invalidateQueries({ queryKey: dispatchQueryKeys.board });
      } catch (error) {
        const known =
          error instanceof ApiError
            ? ASSIGN_REFUSALS[error.code as keyof typeof ASSIGN_REFUSALS]
            : undefined;
        setAssignError(
          t((m) => m.dispatch.handOverAssignFailed, {
            error: known ? t((m) => m.dispatch[known]) : errorMessage(error),
          }),
        );
        if (error instanceof ApiError) {
          void queryClient.invalidateQueries({ queryKey: dispatchQueryKeys.board });
        }
        return;
      }
      }
      await onConfirm({ handedTo: picked.name.trim().slice(0, HANDED_TO_MAX) || null, toPlatform: false });
      return;
    }
    await onConfirm(decision(false));
  };
  const submitting = isSubmitting || busy !== null;
  /* The plain close: always on a delivery; without the name question only
     where there is no platform to hand to (a fallback — the page sends a
     direct takeaway nowhere near this dialog). */
  const offerPlainClose = askWho || !onPlatform;

  return (
    <Modal
      isOpen
      onClose={onCancel}
      size="sm"
      title={t((m) => m.pos.handOverTitle)}
      closeLabel={t((m) => m.common.close)}
      data-testid="hand-over-dialog"
      footerActions={
        <>
          <Button
            variant="outline"
            size="touch"
            disabled={submitting}
            onClick={onCancel}
            data-testid="hand-over-cancel"
          >
            {t((m) => m.common.cancel)}
          </Button>
          {/* The ordinary close: PRIMARY on a direct order, SECONDARY beside
              the platform hand-over — a platform's rider collecting cash is
              the case it stays for. */}
          {offerPlainClose ? (
          <Button
            variant={onPlatform ? "outline" : "primary"}
            size="touch"
            className="font-extrabold"
            disabled={submitting}
            isLoading={submitting && !onPlatform}
            onClick={() => void closePlain()}
            data-testid="hand-over-close"
          >
            {picked && !assigned
              ? t((m) => m.dispatch.handOverAssignClose)
              : assigned?.status === "ACCEPTED"
                ? t((m) => m.dispatch.handOverToDriver)
                : t((m) => m.pos.close)}
          </Button>
          ) : null}
          {onPlatform ? (
            <Button
              variant="primary"
              size="touch"
              className="font-extrabold"
              disabled={submitting}
              isLoading={submitting}
              onClick={() => void onConfirm(decision(true))}
              data-testid="hand-over-platform"
            >
              {t((m) => m.pos.handOverToPlatform, { name: platformName })}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {/* B3 · P14 — the bag's driver, shown, not typed. */}
        {askWho && assigned ? (
          <div
            className="flex flex-col gap-1 rounded-[var(--radius-list)] border border-[color:var(--accent)] px-3 py-2"
            data-testid="hand-over-driver"
            data-status={assigned.status}
          >
            <p className="text-[length:var(--font-size-sm)] font-extrabold text-[color:var(--text)]">
              {t((m) => m.dispatch.handOverDriver, { name: isolate(assigned.driverName) })}
            </p>
            {assigned.status === "OFFERED" ? (
              <p
                className="text-[length:var(--font-size-2xs)] font-bold text-[color:var(--warning)]"
                data-testid="hand-over-driver-offered"
              >
                {t((m) => m.dispatch.handOverDriverOffered, { name: isolate(assigned.driverName) })}
              </p>
            ) : null}
            <p className="text-[length:var(--font-size-2xs)] font-semibold text-[color:var(--text-muted)]">
              {t((m) => m.dispatch.handOverDriverHint)}
            </p>
          </div>
        ) : null}
        {assignError ? (
          <p
            role="alert"
            className="rounded-[var(--radius-list)] bg-[color:var(--danger-100)] px-3 py-2 text-[length:var(--font-size-2xs)] font-bold text-[color:var(--danger)]"
            data-testid="hand-over-assign-error"
          >
            {assignError}
          </p>
        ) : null}
        {askWho && !assigned ? (
        <FormField name="handedTo" label={t((m) => m.pos.handedToLabel)}>
          <TextInput
            name="handedTo"
            value={handedTo}
            maxLength={HANDED_TO_MAX}
            placeholder={t((m) => m.pos.handedToPlaceholder)}
            dir="auto"
            onChange={(event) => {
              setHandedTo(event.target.value);
              // Typing a name is the typed path: no app driver is assigned.
              setPicked(null);
            }}
            data-testid="hand-over-name"
          />
        </FormField>
        ) : null}

        {/* The riders on tonight, as chips — read off the strip's CLOSED
            bags, so the list is exactly who has been handed a bag today and
            nothing is stored to make it so. A tap fills the box; it does not
            close the bill. */}
        {/* B3 · P14 — the drivers on shift: a tap picks one, and the close
            then offers the bag to them first. */}
        {onShift.length > 0 ? (
          <div data-testid="hand-over-drivers">
            <span className="mb-1.5 block text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.06em] text-[color:var(--text-disabled)]">
              {t((m) => m.dispatch.handOverDriversTitle)}
            </span>
            <div
              className="flex flex-wrap gap-1.5"
              role="group"
              aria-label={t((m) => m.dispatch.handOverDriversTitle)}
            >
              {onShift.map((driver) => {
                const isPicked = picked?.sellerId === driver.sellerId;
                return (
                  <button
                    key={driver.sellerId}
                    type="button"
                    aria-pressed={isPicked}
                    disabled={submitting}
                    data-testid={`hand-over-driver-${driver.sellerId}`}
                    onClick={() => {
                      setPicked(isPicked ? null : { sellerId: driver.sellerId, name: driver.name });
                      setHandedTo(isPicked ? "" : driver.name);
                    }}
                    className={[
                      "rounded-[var(--radius-control)] border-[length:var(--border-panel-width,1px)] px-2 py-[5px] text-[length:var(--font-size-2xs)] font-extrabold transition-colors",
                      isPicked
                        ? "border-[color:var(--accent)] bg-[color:var(--accent)] text-[color:var(--accent-contrast)]"
                        : "border-[color:var(--border-strong)] text-[color:var(--text-muted)] hover:text-[color:var(--text)]",
                    ].join(" ")}
                  >
                    <bdi>{driver.name}</bdi>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        {askWho && !assigned && recentNames.length > 0 ? (
          <div>
            <span className="mb-1.5 block text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.06em] text-[color:var(--text-disabled)]">
              {t((m) => m.pos.handedToRecent)}
            </span>
            <div
              className="flex flex-wrap gap-1.5"
              role="group"
              aria-label={t((m) => m.pos.handedToRecent)}
            >
              {recentNames.map((name) => {
                const isCurrent = handedTo.trim() === name;
                return (
                  <button
                    key={name}
                    type="button"
                    aria-pressed={isCurrent}
                    disabled={submitting}
                    data-testid={`hand-over-recent-${name}`}
                    onClick={() => {
                      setHandedTo(name);
                      setPicked(null);
                    }}
                    className={[
                      "rounded-[var(--radius-control)] border-[length:var(--border-panel-width,1px)] px-2 py-[5px] text-[length:var(--font-size-2xs)] font-extrabold transition-colors",
                      isCurrent
                        ? "border-[color:var(--accent)] bg-[color:var(--accent)] text-[color:var(--accent-contrast)]"
                        : "border-[color:var(--border-strong)] text-[color:var(--text-muted)] hover:text-[color:var(--text)]",
                    ].join(" ")}
                  >
                    <bdi>{name}</bdi>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        {/* What the primary action DOES on a platform bill, with the figure:
            the bill closes and is settled by the platform for what is owed,
            and nothing enters the drawer. Said here because a button that
            moves money must say so before it is pressed. */}
        {onPlatform ? (
          <p
            className="text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)]"
            data-testid="hand-over-platform-hint"
          >
            {t((m) => m.pos.handOverToPlatformHint, {
              name: platformName,
              amount: money(due),
            })}
          </p>
        ) : null}
        {/* "We record only who the bag left with" — about the name, so only
            where the name is asked. */}
        {/* B3 · P14: not with an app driver — that bag IS managed and tracked. */}
        {askWho && !assigned && onShift.length === 0 ? (
          <p className="text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)]">
            {t((m) => m.pos.handOverHint)}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
